import * as Sentry from "@sentry/vue";
import moment from "moment";
import { markRaw, reactive } from "vue";

import type { backendOld } from "~/api";
import { UNGROUPED_PSEUDO_GROUP_BY } from "~/common/groupBy";
import {
  getPropertyConfig,
  getPropertyPartialTask,
  getPropertyValueFromTask,
  getPropertyValueFromTaskByDuid,
  getPropertyValueStr,
} from "~/common/properties";
import { sortTasks } from "~/common/sort";
import { checkTaskConstraints } from "~/common/taskConstraints";
import {
  overrideTaskAndAdjustFilters,
  overrideTaskWithDefaults,
  removeFiltersThatDoNotPassForTask,
} from "~/components/filters/utils";
import { COMPLETED_STATUS_KINDS, UNASSIGNED_PSEUDO_ASSIGNEE_KEY } from "~/components/visualization/constants";
import { USER_KIND_DART_AI } from "~/constants/userKinds";
import { isDefined, type TransactionResponse } from "~/shared/common";
import {
  ModelType,
  RelationshipKindKind,
  StatusKind,
  SubtaskDisplayMode,
  TaskLinkKind,
  TaskSourceKind,
  TutorialName,
} from "~/shared/enums";
import type {
  AttachmentCreate,
  Entity,
  FilterGroup,
  Option,
  PropertyAnyMultiselect,
  PropertyAnyUserWithMultipleMaybe,
  PropertyValue,
  Relationship,
  RelationshipCreate,
  RemapModalConfigItem,
  Sort,
  SummaryStatistics,
  Task,
  TaskAbsenteeMaybe,
  TaskAndUpdate,
  TaskCreate,
  TaskDocRelationshipCreate,
  TaskLink,
  TaskLinkCreate,
  TaskLinkUpdate,
  TaskUpdate,
} from "~/shared/types";
import { createFilesFromAttachments } from "~/utils/api";
import {
  deepCopy,
  filterInPlace,
  getItemCountText,
  getNextTitleInSequence,
  isLexicalStateEmpty,
  makeDuid,
  makeEmptyLexicalState,
  prettyFormatList,
} from "~/utils/common";
import { makeStringComparator, stringComparator } from "~/utils/comparator";
import { formatDate } from "~/utils/date";
import { firework } from "~/utils/fun";
import { getOrdersBetween } from "~/utils/orderManager";
import { getUserDisplayName } from "~/utils/user";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const TASK_PAGE_PAGINATION_KEY = "task-pages";

const getPageKey = (pageDuid: string, groupId?: string | null): string => JSON.stringify([pageDuid, groupId ?? null]);
const getPageDuidFromKey = (key: string): string => JSON.parse(key)[0];

type UpdateTaskOptions = {
  noUndo?: boolean;
  noBackend?: boolean;
  noCelebrate?: boolean;
  celebrateOverride?: boolean;
  makeDescription?: (isActive: boolean, content: string) => string;
  overrideFilters?: boolean;
  awaitBackend?: boolean;
  onRemapFailed?: () => void;
  skipRemap?: boolean;
  skipAutomations?: boolean;
};

export type GetTaskOptionsSimple = { includeTrashed?: boolean; includeDraft?: boolean };
export type GetTaskOptions = GetTaskOptionsSimple & { absenteeDuids?: string[] };

export type State = {
  _duidsToTasks: Map<string, Task>;
  taskDraftDuid: string | null;
  _keyToSummaryStatistics: Map<string, SummaryStatistics>;
  _pageDuidToLoadingSummaryStatistics: Map<string, boolean>;
};

export const state: State = {
  _duidsToTasks: new Map(),
  taskDraftDuid: null,
  _keyToSummaryStatistics: new Map(),
  _pageDuidToLoadingSummaryStatistics: new Map(),
};

export type Getters = {
  taskDraft: Task | undefined;
  /* Get the size of a task and its subtasks. */
  getSize: (task: Task, includeSubtasks?: boolean) => number | null;
  /* Get the start and due dates of a task and its subtasks. */
  getTaskDates: (task: Task) => { rolledUpStartAt: string | null; rolledUpDueAt: string | null };
  getTaskByDuid: (duid: string) => Promise<Task | undefined>;
  getTaskByDuidIfLoaded: (duid: string, options?: { assertLoaded?: boolean }) => Task | undefined;
  getTasksByDuids: (duids: string[], options?: GetTaskOptions) => Promise<TaskAbsenteeMaybe[]>;
  // TODO incremental get rid of all of the ...Ordered methods and just use the non-ordered ones
  // the ordering is really bad (just by order). having these methods does more harm than good because devs think that they will actually be ordered in a useful way (e.g. by sorts) but they aren't
  // except getLoadedTasksForPageDuidOrdered, which has the actual ordering
  getTasksByDuidsOrdered: (duids: string[], options?: GetTaskOptions) => Promise<TaskAbsenteeMaybe[]>;
  getTasksByDuidsIfLoaded: (duids: string[], options?: GetTaskOptions) => TaskAbsenteeMaybe[];
  getTasksByDuidsOrderedIfLoaded: (duids: string[], options?: GetTaskOptions) => TaskAbsenteeMaybe[];
  getAllLoadedTasksForUiOnly: (options?: GetTaskOptionsSimple) => Task[];
  getLoadedTasksForPageDuid: (pageDuid: string, groupId?: string, options?: GetTaskOptionsSimple) => Task[];
  getLoadedTasksForPageDuidOrdered: (pageDuid: string, groupId?: string, options?: GetTaskOptionsSimple) => Task[];
  getLoadedTaskCountForPageDuid: (pageDuid: string, groupId?: string) => number;
  getSummaryStatisticsForPageDuid: (pageDuid: string, groupId?: string) => SummaryStatistics;
  hasPageDuidEverBeenLoaded: (pageDuid: string, groupId?: string) => boolean;
  isLoadingSummaryStatisticsForPageDuid: (pageDuid: string) => boolean;
};

export type Actions = {
  loadTasks: (...args: Parameters<typeof backendOld.entityList.task>) => ReturnType<typeof backendOld.entityList.task>;
  loadPaginatedTaskForPageDuid: (
    pageDuid: string,
    filterGroup: FilterGroup,
    sorts: Sort[],
    validator: (task: Task) => boolean,
    options?: {
      groupId?: string;
      search?: string;
      hierarchical?: boolean;
      absentees?: boolean;
      groupByPropertyDuid?: string | null;
      onlyTrash?: boolean;
      includeTrash?: boolean;
    }
  ) => Promise<number>;
  loadSummaryStatisticsForPageDuid: (
    pageDuid: string,
    filterGroup: FilterGroup,
    options?: {
      search?: string;
      groupByPropertyDuid?: string | null;
      onlyTrash?: boolean;
      includeTrash?: boolean;
    }
  ) => Promise<SummaryStatistics>;
  resetPaginationStateForPageDuid: (pageDuid: string, groupId?: string) => void;
  hardDisplayTask: (task: Task, pageDuid: string, groupId?: string) => void;
  isTaskEmpty: (task: Task) => boolean;
  createTask: (
    title: string,
    dartboardDuid: string,
    order: string,
    source: TaskSourceKind,
    partialTask?: Partial<Task>,
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<Task>;
  createTasks: (
    tasks: TaskCreate[],
    source: TaskSourceKind,
    options?: { noUndo?: boolean; relatedDocDuid?: string; awaitBackend?: boolean }
  ) => Promise<Task[]>;
  updateTasks: (updates: TaskUpdate[], options?: UpdateTaskOptions) => Promise<void>;
  updateTaskFromNlp: (
    task: Task,
    update: Partial<Task>,
    options?: { awaitBackend?: boolean; onRemapFailed?: () => void }
  ) => Promise<void>;
  replicateTasks: (
    tasksToReplicate: TaskAndUpdate[],
    options?: {
      noUndo?: boolean;
      propertiesAndParentOnly?: boolean;
      includeDuplicate?: boolean;
      awaitBackend?: boolean;
      maintainTitle?: boolean;
      maintainStatus?: boolean;
    }
  ) => Promise<Task[]>;
  deleteTasks: (tasks: Task[], options?: { awaitBackend?: boolean }) => Promise<void>;
  updateAndDeleteTasks: (updates: TaskUpdate[], deletes: Task[], options?: UpdateTaskOptions) => Promise<void>;
  emptyTrash: () => Promise<void>;
  addUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  removeUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  replaceUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addSubscribers: (
    taskDuids: string[],
    subscriberDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  removeSubscribers: (
    taskDuids: string[],
    subscriberDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; createTitle?: string; awaitBackend?: boolean }
  ) => Promise<TransactionResponse>;
  removeOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  replaceOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addAttachments: (taskDuid: string, files: File[], options?: { awaitBackend?: boolean }) => Promise<void>;
  removeAttachment: (taskDuid: string, attachmentDuid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  addLink: (
    taskDuid: string,
    kind: TaskLinkKind,
    url: string,
    title: string | null,
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  updateLink: (taskDuid: string, linkUpdate: TaskLinkUpdate, options?: { awaitBackend?: boolean }) => Promise<void>;
  deleteLink: (taskDuid: string, linkDuid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  addUpdateAndDeleteLinks: (
    taskDuid: string,
    creates: TaskLinkCreate[],
    updates: TaskLinkUpdate[],
    deletes: string[],
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  addNotionDocument: (
    taskDuid: string,
    pageId: string,
    options?: { awaitBackend?: boolean }
  ) => Promise<Task | undefined>;
  /* Get notion document for task (cached if possible). */
  getNotionDocument: (taskDuid: string) => Promise<Task | undefined>;
  refreshNotionDocument: (taskDuid: string) => Promise<Task | undefined>;
  removeNotionDocument: (taskDuid: string, options?: { awaitBackend?: boolean }) => Promise<Task | undefined>;
  createRelationship: (
    sourceDuid: string,
    targetDuid: string,
    relationshipKindDuid: string,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<Relationship | undefined>;
  deleteRelationship: (
    sourceDuid: string,
    relationshipDuid: string,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  deleteAndCreateRelationshipAndUpdateTask: (
    relationshipDelete?: { taskDuid: string; relationshipDuid: string },
    relationshipCreate?: { sourceDuid: string; targetDuid: string; relationshipKindDuid: string },
    taskUpdate?: TaskUpdate,
    options?: { noBackend?: boolean; celebrateOverride?: boolean; awaitBackend?: boolean; onRemapFailed?: () => void }
  ) => Promise<void>;
  $replicateTaskRecursive: (
    task: Task,
    order: string,
    partialTask?: Partial<Task>,
    options?: {
      propertiesAndParentOnly?: boolean;
      includeDuplicate?: boolean;
      maintainTitle?: boolean;
      maintainStatus?: boolean;
    }
  ) => Promise<{
    replicatedTask: Task;
    taskCreates: TaskCreate[];
    relationshipCreates: RelationshipCreate[];
    taskDocRelationshipCreates: TaskDocRelationshipCreate[];
    linkCreates: TaskLinkCreate[];
    attachmentCreates: AttachmentCreate[];
  }>;
  $createRelationshipOneDirectionNoBackend: (
    relationshipDuid: string,
    modTaskDuid: string,
    otherTaskDuid: string,
    relationshipKindDuid: string,
    isForward?: boolean
  ) => Relationship | undefined;
  $updateTaskSubscribersNoBackend: (task: Task, potentialSubscriberDuids: string[]) => string[];
  $addLinksNoBackend: (task: Task, linkCreates: TaskLinkCreate[]) => void;
  $updateLinksNoBackend: (task: Task, linkUpdates: TaskLinkUpdate[]) => void;
  $deleteLinksNoBackend: (task: Task, linkDuids: string[]) => void;
  $isPrimaryDraft: (task: Task) => boolean;
  $setDraftTask: (task: Task) => void;
  $createOrUpdateTask: (task: Task, extras?: { hardUpdate?: boolean; skipSummaryStatistics?: boolean }) => void;
  $deleteTask: (task: Task) => void;
  /* Remap incompatible properties of tasks using a modal, returns the updated task updates or an empty array if the user cancels */
  $remapPropertiesOfTasksAsNeeded: (updates: TaskUpdate[], onRemapFailed: () => void) => Promise<TaskUpdate[]>;
  $updateSummaryStatisticsForTaskChange: (oldTask: Task | undefined, newTask: Task | undefined) => void;
};

const SINGULAR_TASK_FIELD_NAME_REMAP = new Map([
  ["statusDuid", "status"],
  ["assigneeDuids", "assignee"],
  ["subscriberDuids", "subscriber"],
  ["tagDuids", "tag"],
  ["startAt", "start date"],
  ["dueAt", "due date"],
  ["remindAt", "reminder"],
]);
const PLURAL_TASK_FIELD_NAME_REMAP = new Map([
  ["statusDuid", "statuses"],
  ["priority", "priorities"],
]);

const makeDefaultMakeDescription =
  (field: string | undefined, count: number) => (isActive: boolean, content: string) => {
    if (field === undefined) {
      return `${isActive ? "updating" : "updated"} ${content}`;
    }
    const singularField = SINGULAR_TASK_FIELD_NAME_REMAP.get(field) ?? field;
    const pluralField = PLURAL_TASK_FIELD_NAME_REMAP.get(field) ?? `${field}s`;
    return `set${isActive ? "ting" : ""} ${count !== 1 ? pluralField : singularField} for ${content}`;
  };

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  taskDraft() {
    if (!this.taskDraftDuid) {
      return undefined;
    }

    return this.getTaskByDuidIfLoaded(this.taskDraftDuid);
  },
  getTasksByDuidsIfLoaded() {
    return (duids, options = {}) => {
      const { includeTrashed = false, includeDraft = false, absenteeDuids = [] } = options;
      const getAll = (duidsInner: string[]) =>
        [...new Set(duidsInner)]
          .map((e) => this.getTaskByDuidIfLoaded(e))
          .filter(isDefined)
          .filter((e) => (includeTrashed || !e.inTrash) && (includeDraft || !e.drafterDuid));
      const absentees = getAll(absenteeDuids).map((e) => ({ ...e, absentee: true }));
      return [...getAll(duids), ...absentees];
    };
  },
  getTasksByDuidsOrderedIfLoaded() {
    return (duids, options) => this.getTasksByDuidsIfLoaded(duids, options).sort(makeStringComparator((e) => e.order));
  },
  getSize() {
    return (task, includeSubtasks = false) => {
      const taskSize = task.size ?? null;
      if (!includeSubtasks) {
        return taskSize;
      }

      const subtasksSize = this.getDescendantDuidsIfLoaded(task).reduce(
        (total, duid) => total + (this.getTaskByDuidIfLoaded(duid)?.size ?? 0),
        0
      );

      return taskSize !== null ? taskSize + subtasksSize : subtasksSize > 0 ? subtasksSize : null;
    };
  },
  getTaskDates() {
    return (task: Task) => {
      if (!task) {
        return { rolledUpStartAt: null, rolledUpDueAt: null };
      }

      if (task.startAt && task.dueAt) {
        return { rolledUpStartAt: task.startAt, rolledUpDueAt: task.dueAt };
      }

      const allRelatedTasks = [
        task,
        ...this.getDescendantDuidsIfLoaded(task)
          .map((e) => this.getTaskByDuidIfLoaded(e))
          .filter(isDefined),
      ];

      const earliestStartAt =
        task.startAt ||
        allRelatedTasks.reduce(
          (earliest, currentTask) =>
            currentTask?.startAt && (!earliest || moment(currentTask.startAt).isBefore(earliest))
              ? currentTask.startAt
              : earliest,
          null as string | null
        );

      const latestDueAt =
        task.dueAt ||
        allRelatedTasks.reduce(
          (latest, currentTask) =>
            currentTask?.dueAt && (!latest || moment(currentTask.dueAt).isAfter(latest)) ? currentTask.dueAt : latest,
          null as string | null
        );

      return { rolledUpStartAt: earliestStartAt, rolledUpDueAt: latestDueAt };
    };
  },
  getTaskByDuid() {
    return async (duid) => {
      const task = this.getTaskByDuidIfLoaded(duid);
      if (task) {
        return task;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const taskFetched = await this.$batchers.entityListManager.getEntityByDuid("tasks", duid);
      if (!taskFetched) {
        this.$blacklistEntityDuid("tasks", duid);
        return undefined;
      }

      this.$createOrUpdateTask(taskFetched, { skipSummaryStatistics: true });
      return taskFetched;
    };
  },
  getTaskByDuidIfLoaded() {
    return (duid, options = {}) => {
      const { assertLoaded = false } = options;
      const task = this._duidsToTasks.get(duid);
      if (assertLoaded && !task) {
        Sentry.captureMessage(`Assert loaded task with duid not found: ${duid}`);
      }
      return task;
    };
  },
  getTasksByDuids() {
    return async (duids, options = {}) => {
      const { includeTrashed = false, includeDraft = false, absenteeDuids = [] } = options;
      const getAll = async (duidsInner: string[]) => {
        const tasks = await Promise.all([...new Set(duidsInner)].map((e) => this.getTaskByDuid(e)));
        return tasks
          .filter(isDefined)
          .filter((e) => (includeTrashed || !e.inTrash) && (includeDraft || !e.drafterDuid));
      };

      const [mainTasks, absentees] = await Promise.all([getAll(duids), getAll(absenteeDuids)]);
      return [...mainTasks, ...absentees.map((e) => ({ ...e, absentee: true }))];
    };
  },
  getTasksByDuidsOrdered() {
    return async (duids, options = {}) => {
      const tasks = await this.getTasksByDuids(duids, options);
      return tasks.sort(makeStringComparator((e) => e.order));
    };
  },
  getAllLoadedTasksForUiOnly() {
    return (options = {}) =>
      [...this._duidsToTasks.values()].filter(
        (e) => (options.includeTrashed || !e.inTrash) && (options.includeDraft || !e.drafterDuid)
      );
  },
  getLoadedTasksForPageDuid() {
    return (pageDuid, groupId, options = {}) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);

      if (!paginationState) {
        return [];
      }

      // Return a temporary list of tasks while initializing for better UX
      if (!paginationState.loadedAll && paginationState.duids.size === 0) {
        return [...this.getAllLoadedTasksForUiOnly(options)].filter((e) => paginationState.validator?.(e));
      }

      return [...paginationState.duids.values()]
        .map((e) => this.getTaskByDuidIfLoaded(e))
        .filter(isDefined)
        .filter((e) => paginationState.validator?.(e))
        .filter((task) => (options.includeTrashed || !task.inTrash) && (options.includeDraft || !task.drafterDuid));
    };
  },
  getLoadedTasksForPageDuidOrdered() {
    return (pageDuid, groupId, options = {}) => {
      const appStore = this.$useAppStore();
      const shouldApplyGroupSort =
        groupId === undefined && appStore.groupBy && appStore.groupBy !== UNGROUPED_PSEUDO_GROUP_BY;
      return sortTasks(
        this,
        this.getLoadedTasksForPageDuid(pageDuid, groupId, options),
        appStore.sorts,
        shouldApplyGroupSort ? appStore.groupByDefinition : null,
        appStore.subtaskDisplayMode === SubtaskDisplayMode.FLAT
      );
    };
  },
  getLoadedTaskCountForPageDuid() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);

      if (!paginationState) {
        return 0;
      }

      return paginationState.duids.size;
    };
  },
  getSummaryStatisticsForPageDuid() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      return this._keyToSummaryStatistics.get(key) ?? { count: 0, points: 0, completedCount: 0, completedPoints: 0 };
    };
  },
  isLoadingSummaryStatisticsForPageDuid() {
    return (pageDuid) => this._pageDuidToLoadingSummaryStatistics.get(pageDuid) ?? false;
  },
  hasPageDuidEverBeenLoaded() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);
      return !!paginationState;
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadTasks(...args) {
    const response = await this.$backendOld.entityList.task(...args);
    response.data.results.forEach((task) => {
      if (this.getTaskByDuidIfLoaded(task.duid)) {
        return;
      }
      this.$createOrUpdateTask(task, { skipSummaryStatistics: true });
    });
    return response;
  },
  async loadPaginatedTaskForPageDuid(pageDuid, filterGroup, sorts, validator, options = {}) {
    const {
      groupId = null,
      search = "",
      hierarchical = false,
      absentees = false,
      groupByPropertyDuid = null,
      onlyTrash = false,
      includeTrash = false,
    } = options;
    const pageStore = this.$usePageStore();
    const key = getPageKey(pageDuid, groupId);

    let paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);
    const { data } = await (pageStore.isPublicView
      ? this.$backendOld.entityList.getPublicViewTasks(pageDuid, filterGroup, paginationState?.duids.size ?? 0, {
          hierarchical,
          absentees,
          groupByPropertyDuid,
        })
      : this.$backendOld.entityList.queryTasks(filterGroup, sorts, search, paginationState?.duids.size ?? 0, {
          hierarchical,
          absentees,
          groupByPropertyDuid,
          onlyTrash,
          includeTrash,
        }));
    data.results.forEach((task) => {
      if (this.getTaskByDuidIfLoaded(task.duid)) {
        return;
      }
      this.$createOrUpdateTask(task, { skipSummaryStatistics: true });
    });

    this._keyToSummaryStatistics.set(key, data.summaryStatistics);

    if (!paginationState) {
      paginationState = this.$createPaginationState(
        TASK_PAGE_PAGINATION_KEY,
        key,
        // TODO incremental, make a better sort comparator, accept all for now. I guess this is the same as the old "TODO why are task and notification comparators inverted?" https://github.com/its-dart/dart/pull/2805/files/a6d4657495ad6e674cf05f59060d2733c8586ef6#r2249515223
        () => 0,
        validator as (e: Entity) => boolean
      );
    } else {
      // TODO incremental reevaluate this
      // Update the validator when reusing an existing pagination state
      // This is crucial when filters change and we reset the pagination
      paginationState.validator = validator as (e: Entity) => boolean;
    }
    this.$addEntitiesToPaginationState(TASK_PAGE_PAGINATION_KEY, key, data.results, data.count);

    return data.count;
  },
  async loadSummaryStatisticsForPageDuid(pageDuid, filterGroup, options) {
    const { groupByPropertyDuid = null, search = "", onlyTrash = false, includeTrash = false } = options ?? {};
    const pageStore = this.$usePageStore();

    this._pageDuidToLoadingSummaryStatistics.set(pageDuid, true);
    const { data } = await (pageStore.isPublicView
      ? this.$backendOld.entityList.getPublicViewSummaryStatistics(pageDuid, filterGroup, {
          groupByPropertyDuid,
        })
      : this.$backendOld.entityList.getSummaryStatistics(filterGroup, {
          search,
          groupByPropertyDuid,
          onlyTrash,
          includeTrash,
        }));

    this._keyToSummaryStatistics.set(getPageKey(pageDuid), data.overallStatistics);
    if (data.groupedStatistics) {
      Object.entries(data.groupedStatistics).forEach(([groupId, stats]) => {
        const groupKey = getPageKey(pageDuid, groupId);
        this._keyToSummaryStatistics.set(groupKey, stats);
      });
    }

    this._pageDuidToLoadingSummaryStatistics.set(pageDuid, false);

    return data.overallStatistics;
  },
  resetPaginationStateForPageDuid(pageDuid, groupId) {
    const key = getPageKey(pageDuid, groupId);
    this.$clearPaginationState(TASK_PAGE_PAGINATION_KEY, key);
    // Clear all summary statistics for this page
    [...this._keyToSummaryStatistics.keys()]
      .filter((e) => getPageDuidFromKey(e) === pageDuid)
      .forEach((e) => this._keyToSummaryStatistics.delete(e));
  },
  hardDisplayTask(task, pageDuid, groupId) {
    const key = getPageKey(pageDuid, groupId);
    this.$addEntitiesToPaginationState(TASK_PAGE_PAGINATION_KEY, key, [task], null);
  },
  isTaskEmpty(task) {
    return (
      task.title.trim() === "" &&
      isLexicalStateEmpty(task.description) &&
      task.tagDuids.length === 0 &&
      task.links.length === 0 &&
      task.attachmentDuids.length === 0 &&
      this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, true).length === 0 &&
      task.priority === null &&
      task.size === null &&
      task.startAt === null &&
      task.dueAt === null &&
      task.notionDocument == null
    );
  },
  async createTask(title, dartboardDuid, order, source, partialTask, options) {
    const tasks = await this.createTasks(
      [
        {
          duid: makeDuid(),
          title,
          dartboardDuid,
          order,
          ...partialTask,
        },
      ],
      source,
      options
    );
    return tasks[0];
  },
  async createTasks(tasks, source, options = {}) {
    const { noUndo = false, relatedDocDuid, awaitBackend = false } = options;

    const appStore = this.$useAppStore();
    const userStore = this.$useUserStore();

    const taskKindDuid = this.defaultTaskKind.duid;
    const statusDuid = this.defaultDefaultUnstartedStatus.duid;
    const userDuid = userStore.duid;
    const now = new Date().toISOString();
    const newTasks = tasks.map((taskCreate) => {
      const taskWithDefaults = overrideTaskWithDefaults(this, { ...taskCreate, sourceKind: source });

      const task: Task = {
        sourceKind: source,
        createdAt: now,
        createdByDuid: userDuid,
        updatedAt: now,
        updatedByDuid: userDuid,
        drafterDuid: null,
        inTrash: false,
        recommendationDuid: null,
        dartboardDuid: "",
        order: "",
        expanded: true,
        kindDuid: taskKindDuid,
        title: "",
        description: makeEmptyLexicalState(),
        statusDuid,
        assigneeDuids: [userDuid],
        subscriberDuids: [userDuid],
        tagDuids: [],
        relationships: [],
        docRelationships: [],
        links: [],
        attachmentDuids: [],
        priority: null,
        size: null,
        startAt: null,
        dueAt: null,
        timeTracking: [],
        remindAt: null,
        recurrence: null,
        recursNextAt: null,
        properties: {},
        notionDocument: null,
        ...taskWithDefaults,
      };

      const overriddenTask = overrideTaskAndAdjustFilters(this, task, taskCreate, source);
      if (overriddenTask.assigneeDuids) {
        overriddenTask.subscriberDuids = [
          ...new Set([...overriddenTask.subscriberDuids, ...overriddenTask.assigneeDuids]),
        ];
      }

      const suggestedUpdate = checkTaskConstraints(overriddenTask, this);
      if (Object.keys(suggestedUpdate).length > 0) {
        Object.assign(overriddenTask, suggestedUpdate);
      }

      this.$createOrUpdateTask(overriddenTask);
      appStore.addRecentTask(overriddenTask);
      this._tasksDuidsWithoutComments.add(overriddenTask.duid);
      return overriddenTask;
    });

    const newTaskRelationships = newTasks.flatMap((newTask) =>
      newTask.relationships
        .map(({ duid, kindDuid, targetDuid, isForward }) => [
          this.$createRelationshipOneDirectionNoBackend(
            duid,
            targetDuid,
            newTask.duid,
            kindDuid,
            isForward !== undefined ? !isForward : undefined
          ),
          targetDuid,
        ])
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }))
    );

    const newDocRelationships = relatedDocDuid
      ? newTasks.map((newTask) => {
          const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid: newTask.duid, docDuid: relatedDocDuid };
          this.$createTaskDocRelationship(taskDocRelationshipCreate);
          return taskDocRelationshipCreate;
        })
      : [];

    const backendAction = this.$backend.task.createMany(newTasks, newTaskRelationships, newDocRelationships);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const content = newTasks.length > 1 ? getItemCountText(newTasks.length, "task") : newTasks[0].title;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: newTasks.map((e) => e.duid) },
        [`created ${content} tasks`, `creating ${content}`],
        () => this.deleteTasks(newTasks),
        () => this.createTasks(tasks, source, { noUndo: true })
      );
    }

    if (newTasks.some((e) => e.drafterDuid === null)) {
      userStore.updateTutorialStatuses([{ name: TutorialName.MAKE_A_TASK, status: 2 }], false, true);
    }

    return newTasks;
  },
  async updateTasks(updates, options) {
    await this.updateAndDeleteTasks(updates, [], options);
  },
  async updateTaskFromNlp(task, update, options) {
    const { duid, assigneeDuids, tagDuids, subscriberDuids } = task;
    const updateNorm: TaskUpdate = {
      ...update,
      tagDuids: [...new Set([...tagDuids, ...(update.tagDuids ?? [])])],
      duid,
      title: update.title ?? "",
    };
    if (update.assigneeDuids && update.assigneeDuids.length > 0) {
      updateNorm.subscriberDuids = [...new Set([...subscriberDuids, ...update.assigneeDuids])];
      if (this.$useTenantStore().multipleAssigneesEnabled) {
        updateNorm.assigneeDuids = [...new Set([...assigneeDuids, ...update.assigneeDuids])];
      }
    }
    await this.updateTasks([updateNorm], { ...options, noUndo: true });
  },
  async replicateTasks(tasksToReplicate, options = {}) {
    const {
      noUndo = false,
      propertiesAndParentOnly = false,
      includeDuplicate = false,
      awaitBackend = false,
      maintainTitle = false,
      maintainStatus = false,
    } = options;

    if (tasksToReplicate.length === 0) {
      return [];
    }

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;

    let filteredTasksToReplicate = tasksToReplicate;
    if (!propertiesAndParentOnly) {
      const taskDuids = new Set(tasksToReplicate.map((e) => e.task.duid));
      filteredTasksToReplicate = filteredTasksToReplicate.filter(({ task }) =>
        this.getAncestorDuidsIfLoaded(task).every((ancestor) => !taskDuids.has(ancestor))
      );
    }

    const tasksToCreate: TaskCreate[] = [];
    const relationshipsToCreate: RelationshipCreate[] = [];
    const taskDocRelationshipsToCreate: TaskDocRelationshipCreate[] = [];
    const linksToCreate: TaskLinkCreate[] = [];
    const attachmentsToCreate: AttachmentCreate[] = [];

    const replicatedTasks: Task[] = [];
    await Promise.all(
      filteredTasksToReplicate.map(async ({ task, order, partialTask }) => {
        const normPartialTask = partialTask ?? {};
        normPartialTask.relationships = normPartialTask.relationships ?? [];

        const parentRelationship = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false)[0];
        if (parentRelationship && !normPartialTask.relationships.some((e) => e.kindDuid === parentKindDuid)) {
          // TODO unclear why reactive is needed here. should figure it out and remove
          const replicateParentRelationship = reactive({ ...parentRelationship, duid: makeDuid() });
          normPartialTask.relationships.push(replicateParentRelationship);
        }

        const {
          replicatedTask,
          taskCreates,
          relationshipCreates,
          taskDocRelationshipCreates,
          linkCreates,
          attachmentCreates,
        } = await this.$replicateTaskRecursive(task, order, normPartialTask, {
          propertiesAndParentOnly,
          includeDuplicate,
          maintainTitle,
          maintainStatus,
        });

        replicatedTasks.push(replicatedTask);
        tasksToCreate.push(...taskCreates);
        relationshipsToCreate.push(...relationshipCreates);
        taskDocRelationshipsToCreate.push(...taskDocRelationshipCreates);
        linksToCreate.push(...linkCreates);
        attachmentsToCreate.push(...attachmentCreates);

        if (this.$isPrimaryDraft(replicatedTask)) {
          this.taskDraftDuid = replicatedTask.duid;
        }
      })
    );

    const taskCreateAction = this.$backend.task.createMany(
      tasksToCreate,
      relationshipsToCreate,
      taskDocRelationshipsToCreate,
      linksToCreate,
      attachmentsToCreate
    );

    if (awaitBackend) {
      await taskCreateAction;
    }

    const content =
      replicatedTasks.length > 1
        ? getItemCountText(replicatedTasks.length, "task")
        : `task ${replicatedTasks[0].title}`;

    if (!noUndo) {
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: replicatedTasks.map((e) => e.duid) },
        [`Replicated ${content}`, `replicating ${content}`],
        async () => this.deleteTasks(await this.getTasksByDuidsOrdered(tasksToCreate.map((e) => e.duid))),
        () => this.replicateTasks(tasksToReplicate, options)
      );
    }

    return replicatedTasks;
  },
  async deleteTasks(tasks, options) {
    this.updateAndDeleteTasks([], tasks, { ...options, noUndo: true });
  },
  async emptyTrash() {
    this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true })
      .filter((e) => e.inTrash)
      .forEach((e) => {
        this.$deleteTask(e);
      });
    await this.$backendOld.tasks.emptyTrash();
  },
  async updateAndDeleteTasks(updates, deletes, options = {}) {
    const {
      noUndo = false,
      noBackend = false,
      noCelebrate = false,
      celebrateOverride = false,
      makeDescription,
      overrideFilters = false,
      awaitBackend = false,
      onRemapFailed = () => {},
      skipRemap = false,
      skipAutomations = false,
    } = options;

    // Remap can only be skipped if noBackend is true
    if (!(skipRemap && noBackend)) {
      // eslint-disable-next-line no-param-reassign
      updates = await this.$remapPropertiesOfTasksAsNeeded(updates, onRemapFailed);
    }

    if (updates.length === 0 && deletes.length === 0) {
      return;
    }

    const appStore = this.$useAppStore();
    const tenantStore = this.$useTenantStore();
    const userStore = this.$useUserStore();
    const updaterDuid = userStore.duid;

    const automatedUpdates: TaskUpdate[] = [];
    if (!skipAutomations && tenantStore.closeParentOnCloseAllSubtasks) {
      const completedStatusDuids = new Set(
        this.getStatusesByKinds([...COMPLETED_STATUS_KINDS], this.defaultStatusProperty).map((e) => e.duid)
      );
      const newlyCompletedDuidsList = updates
        .filter((e) => e.statusDuid && completedStatusDuids.has(e.statusDuid))
        .map((e) => e.duid);
      const newlyTrashedDuidsList = updates.filter((e) => e.inTrash).map((e) => e.duid);

      const finishedStatusDuid = this.defaultDefaultFinishedStatus.duid;
      const newlyCompletedDuids = new Set(newlyCompletedDuidsList);
      const newlyTrashedDuids = new Set(newlyTrashedDuidsList);
      const queue = this.getTasksByDuidsIfLoaded([...newlyCompletedDuids, ...newlyTrashedDuids]);
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const parent = this.getParentTaskIfLoaded(curr);
        if (!parent || completedStatusDuids.has(parent.statusDuid) || newlyCompletedDuids.has(parent.duid)) {
          continue;
        }
        const siblings = this.getTasksByDuidsIfLoaded(
          this.getRelationshipsByKindKind(parent, RelationshipKindKind.PARENT_OF, true).map((e) => e.targetDuid)
        );
        if (
          siblings.length === 0 ||
          siblings.some(
            (e) =>
              !completedStatusDuids.has(e.statusDuid) &&
              !newlyCompletedDuids.has(e.duid) &&
              !newlyTrashedDuids.has(e.duid)
          )
        ) {
          continue;
        }
        newlyCompletedDuids.add(parent.duid);
        queue.push(parent);
        automatedUpdates.push({ duid: parent.duid, statusDuid: finishedStatusDuid });
      }
    }
    if (!skipAutomations && tenantStore.startParentOnStartAnySubtask) {
      const unstartedStatusDuids = new Set(
        this.getStatusesByKinds([StatusKind.UNSTARTED], this.defaultStatusProperty).map((e) => e.duid)
      );
      const startedStatusDuids = new Set(
        this.getStatusesByKinds([StatusKind.STARTED], this.defaultStatusProperty).map((e) => e.duid)
      );
      const newlyStartedDuidsList = updates
        .filter((e) => e.statusDuid && startedStatusDuids.has(e.statusDuid))
        .map((e) => e.duid);
      const newlyStartedDuids = new Set(newlyStartedDuidsList);
      const queue = this.getTasksByDuidsIfLoaded([...newlyStartedDuids]);
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const parent = this.getParentTaskIfLoaded(curr);
        if (!parent || !unstartedStatusDuids.has(parent.statusDuid) || newlyStartedDuids.has(parent.duid)) {
          continue;
        }

        const currUpdate = [...updates, ...automatedUpdates].find((update) => update.duid === curr.duid);
        const currStatusDuid = currUpdate?.statusDuid ?? curr.statusDuid;

        automatedUpdates.push({ duid: parent.duid, statusDuid: currStatusDuid });
        newlyStartedDuids.add(parent.duid);
        queue.push(parent);
      }
    }
    if (!skipAutomations && tenantStore.moveSubtasksOnMoveParent) {
      updates.forEach((update) => {
        if (!update.dartboardDuid) {
          return;
        }
        const oldTask = this.getTaskByDuidIfLoaded(update.duid);
        if (!oldTask) {
          return;
        }
        const oldDartboardDuid = oldTask.dartboardDuid;
        this.getTasksByDuidsIfLoaded(
          this.getSourceOrTargetDuidsUiOnly(oldTask, RelationshipKindKind.PARENT_OF, false, true)
        ).forEach((task) => {
          if (task.dartboardDuid !== oldDartboardDuid) {
            return;
          }
          automatedUpdates.push({ duid: task.duid, dartboardDuid: update.dartboardDuid });
        });
      });
    }
    if (!skipAutomations && tenantStore.updateSubtasksStatusOnUpdateParentStatus) {
      updates.forEach((update) => {
        if (!update.statusDuid) {
          return;
        }
        const oldTask = this.getTaskByDuidIfLoaded(update.duid);
        if (!oldTask) {
          return;
        }
        const newStatus = this.getStatusByDuid(update.statusDuid);
        if (!newStatus || !COMPLETED_STATUS_KINDS.has(newStatus.kind)) {
          return;
        }

        this.getTasksByDuidsIfLoaded(
          this.getSourceOrTargetDuidsUiOnly(oldTask, RelationshipKindKind.PARENT_OF, false, true)
        ).forEach((task) => {
          const taskStatus = this.getStatusByDuid(task.statusDuid);
          if (!taskStatus || COMPLETED_STATUS_KINDS.has(taskStatus.kind)) {
            return;
          }

          automatedUpdates.push({ duid: task.duid, statusDuid: update.statusDuid });
        });
      });
    }
    if (!skipAutomations && tenantStore.updateBlockeeDatesOnUpdateBlockerDueDate) {
      const newlyUpdatedDuidToDueAt = new Map(
        updates
          .filter((e) => e.dueAt)
          .map(({ duid, startAt, dueAt }) => [duid, { startAt: startAt ?? undefined, dueAt: dueAt ?? undefined }])
      );

      const originalUpdateDuidsList = new Set(newlyUpdatedDuidToDueAt.keys());
      const queue = this.getTasksByDuidsIfLoaded([...originalUpdateDuidsList]);
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const dueAt = newlyUpdatedDuidToDueAt.get(curr.duid)?.dueAt;
        if (!dueAt) {
          continue;
        }

        this.getTasksByDuidsOrderedIfLoaded(
          this.getRelationshipsByKindKind(curr, RelationshipKindKind.BLOCKS, true).map((e) => e.targetDuid)
        ).forEach((blockee) => {
          const currentStartDueAt = newlyUpdatedDuidToDueAt.get(blockee.duid);
          const currentStartAt = currentStartDueAt?.startAt ?? blockee.startAt;
          const currentDueAt = currentStartDueAt?.dueAt ?? blockee.dueAt;
          if (!currentStartAt) {
            if (currentDueAt && currentDueAt < dueAt) {
              newlyUpdatedDuidToDueAt.set(blockee.duid, { startAt: undefined, dueAt });
              queue.push(blockee);
            }
            return;
          }
          if (currentStartAt > dueAt) {
            return;
          }
          const newDueAt = currentDueAt
            ? formatDate(moment(dueAt).add(moment(currentDueAt).diff(currentStartAt)))
            : undefined;
          newlyUpdatedDuidToDueAt.set(blockee.duid, { startAt: dueAt, dueAt: newDueAt });
          if (newDueAt) {
            queue.push(blockee);
          }
        });
      }

      automatedUpdates.push(
        ...[...newlyUpdatedDuidToDueAt.entries()]
          .filter(([duid]) => !originalUpdateDuidsList.has(duid))
          .map(([duid, e]) => ({ duid, ...e }))
      );
    }
    updates.push(...automatedUpdates.map((e) => ({ ...e, automatedUpdate: true })));

    const subscriberListAdds: TaskUpdate[] = [];
    const inverseUpdates: TaskUpdate[] = [];

    await this.getTasksByDuids(
      updates.map((e) => e.duid),
      { includeDraft: true }
    );
    updates.forEach((update) => {
      const task = this.getTaskByDuidIfLoaded(update.duid);
      if (!task) {
        return;
      }
      const oldTask = { ...task };

      if (overrideFilters) {
        removeFiltersThatDoNotPassForTask(this, { ...task, ...update });
      }

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      const inverseUpdate: TaskUpdate = { duid: task.duid };

      // eslint-disable-next-line no-restricted-syntax
      for (const [field, value] of Object.entries(update)) {
        if (field === "duid") {
          continue;
        }
        if (field === "inTrash" && value === true) {
          appStore.selectedTaskDuids.delete(task.duid);
        }
        if (
          !noCelebrate &&
          field === "statusDuid" &&
          this.getStatusByDuid(value as string)?.kind === StatusKind.FINISHED &&
          (this.getStatusByDuid(task.statusDuid)?.kind !== StatusKind.FINISHED || celebrateOverride)
        ) {
          firework();
        }

        const fieldAsKeyOfTask = field as keyof Task;
        (inverseUpdate[field as keyof TaskUpdate] as unknown) = task[fieldAsKeyOfTask];
        (task[fieldAsKeyOfTask] as unknown) = value;
      }

      if (task.inTrash !== undefined && task.inTrash === update.inTrash) {
        this.$updateSummaryStatisticsForTaskChange(oldTask, task);
        this.$removeEntitiesFromPaginationStates([task]);
      }

      if (update.dartboardDuid && update.dartboardDuid !== oldTask.dartboardDuid) {
        this.$updateSummaryStatisticsForTaskChange(oldTask, task);
        this.$removeEntitiesFromPaginationStates([task]);
      }

      inverseUpdates.push(inverseUpdate);
      task.updatedAt = new Date().toISOString();
      task.updatedByDuid = updaterDuid;

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);

      // Also re-validate all ancestors since they might be absentee parents that no longer qualify
      const ancestorDuids = this.getAncestorDuidsIfLoaded(task);
      const ancestors = this.getTasksByDuidsIfLoaded(ancestorDuids);
      ancestors.forEach((ancestor) => {
        this.$maybeRemoveInvalidEntityFromPaginationStates(ancestor);
      });

      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }

      appStore.addRecentTask(task);

      if (this.$isPrimaryDraft(task)) {
        this.taskDraftDuid = task.duid;
      } else if (task.duid === this.taskDraft?.duid) {
        this.taskDraftDuid = null;
        userStore.updateTutorialStatuses([{ name: TutorialName.MAKE_A_TASK, status: 2 }], false, true);
      }

      const suggestedUpdate = checkTaskConstraints(update, this);
      if (Object.keys(suggestedUpdate).length > 0) {
        Object.assign(update, suggestedUpdate);
        Sentry.captureMessage("Task constraints violated");
      }
    });

    deletes.forEach((task) => {
      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      this.$updateSummaryStatisticsForTaskChange(task, undefined);
      this.$removeEntitiesFromPaginationStates([task]);
      this.$deleteTask(task);
    });

    if (!noBackend) {
      const backendAction = this.$backend.task.updateUpdateListAddAndDeleteMany(
        updates.filter((e) => !("automatedUpdate" in e) || !e.automatedUpdate),
        deletes.map((e) => e.duid),
        subscriberListAdds
      );

      deletes.forEach((e) => this.$addEntityDuidPendingDeletion(e.duid));
      const action = (async () => {
        await backendAction;
        deletes.forEach((e) => this.$removeEntityDuidPendingDeletion(e.duid));
      })();

      if (awaitBackend) {
        await action;
      }
    }

    if (!noUndo) {
      const fields = [...new Set(updates.flatMap((e) => Object.keys(e)))].filter((e) => e !== "duid");
      const field = fields.length === 0 ? fields[0] : undefined;

      await this.getTasksByDuids(
        updates.map((e) => e.duid),
        { includeDraft: true }
      );
      const tasks = [...new Set(updates.map((e) => e.duid))]
        .map((e) => this.getTaskByDuidIfLoaded(e))
        .filter(isDefined);
      const content = tasks.length === 1 ? tasks[0].title : getItemCountText(tasks.length, "task");
      const makeDescriptionNorm = makeDescription ?? makeDefaultMakeDescription(field, tasks.length);
      const descriptionPast = makeDescriptionNorm(false, content);
      const descriptionActive = makeDescriptionNorm(true, content);

      this.saveToUndo(
        {
          modelType: ModelType.TASK,
          duid: updates.map((u) => u.duid),
          field,
        },
        [descriptionPast, descriptionActive],
        () => this.updateTasks(inverseUpdates, { noUndo: true, noBackend }),
        () => this.updateTasks(updates, { noUndo: true, noBackend })
      );
    }
  },
  async addUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = this.$useUserStore().duid;

    const taskUpdates: TaskUpdate[] = [];
    const subscriberListAdds: TaskUpdate[] = [];

    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });
    tasks.forEach((task) => {
      const currentUserDuids = getPropertyValueFromTask(property, task);
      const newUserDuidsOrdered = this.getUsersByDuidsOrdered([...new Set([...currentUserDuids, ...userDuids])]).map(
        (e) => e.duid
      );
      const partialTaskUpdate = getPropertyPartialTask(property, task, newUserDuidsOrdered);
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      const taskUpdate: TaskUpdate = {
        duid: task.duid,
        ...(isDefault ? { assigneeDuids: userDuids } : partialTaskUpdate),
      };

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid, ...userDuids]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      taskUpdates.push(taskUpdate);
    });

    let backendAction: Promise<TransactionResponse>;

    if (isDefault) {
      backendAction = this.$backend.task.updateListAddMany([...taskUpdates, ...subscriberListAdds]);
    } else {
      backendAction = this.$backend.task.updateAndUpdateListAddMany(taskUpdates, subscriberListAdds);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedUsersStr = prettyFormatList(this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e)));

      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `${isDefault ? "Assigned" : `Added ${property.title.toLowerCase()}`} ${addedUsersStr} to ${content}`,
          `${isDefault ? "assigning" : `adding ${property.title.toLowerCase()}`} ${addedUsersStr} to ${content}`,
        ],
        () => this.removeUsers(property, taskDuids, userDuids, { noUndo: true }),
        () => this.addUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async removeUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = this.$useUserStore().duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      const currentUserDuids = getPropertyValueFromTask(property, task);
      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        currentUserDuids.filter((e) => !userDuids.includes(e))
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, ...(isDefault ? { assigneeDuids: userDuids } : partialTaskUpdate) });
    });

    let backendAction: Promise<TransactionResponse>;
    // TODO subscribe updater
    if (isDefault) {
      backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    } else {
      backendAction = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedUsersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e))
      );

      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `${isDefault ? "Unassigned" : `Removed ${property.title.toLowerCase()}`} ${removedUsersStr} from ${content}`,
          `${
            isDefault ? "unassigning" : `removing ${property.title.toLowerCase()}`
          } ${removedUsersStr} from ${content}`,
        ],
        () => this.addUsers(property, taskDuids, userDuids, { noUndo: true }),
        () => this.removeUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async replaceUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = this.$useUserStore().duid;

    const taskUpdates: TaskUpdate[] = [];
    const subscriberListAdds: TaskUpdate[] = [];

    const oldUserDuids: string[] = [];

    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });
    tasks.forEach((task) => {
      const currentUserDuids = getPropertyValueFromTask(property, task);
      oldUserDuids.push(...currentUserDuids);
      const newUserDuidsOrdered = this.getUsersByDuidsOrdered([...new Set(userDuids)]).map((e) => e.duid);
      const partialTaskUpdate = getPropertyPartialTask(property, task, newUserDuidsOrdered);
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid, ...userDuids]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      taskUpdates.push({ duid: task.duid, ...partialTaskUpdate });
    });

    const backendAction = this.$backend.task.updateAndUpdateListAddMany(taskUpdates, subscriberListAdds);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedUsersStr = prettyFormatList(this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e)));
      const pastAction = userDuids
        ? `${isDefault ? "Assigned" : `Added ${property.title.toLowerCase()}`} ${addedUsersStr} to`
        : isDefault
          ? "Unassigned"
          : "Removed";
      const presentAction = userDuids
        ? `${isDefault ? "assigning" : `adding ${property.title.toLowerCase()}`} ${addedUsersStr} to`
        : isDefault
          ? "unassigning"
          : "removing";
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [`${pastAction} ${content}`, `${presentAction} ${content}`],
        () => this.replaceUsers(property, taskDuids, oldUserDuids, { noUndo: true }),
        () => this.replaceUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async addSubscribers(taskDuids, subscriberDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const updaterDuid = this.$useUserStore().duid;

    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      // eslint-disable-next-line no-param-reassign
      task.subscriberDuids = [...new Set([...task.subscriberDuids, ...subscriberDuids])];
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, subscriberDuids });
    });

    const backendAction = this.$backend.task.updateListAddMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedSubscribersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(subscriberDuids).map((e) => getUserDisplayName(e))
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: "subscriberDuids" },
        [`Subscribed ${addedSubscribersStr} to ${content}`, `subscribing ${addedSubscribersStr} to ${content}`],
        () => this.removeSubscribers(taskDuids, subscriberDuids, { noUndo: true }),
        () => this.addSubscribers(taskDuids, subscriberDuids, { noUndo: true })
      );
    }
  },
  async removeSubscribers(taskDuids, subscriberDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const updaterDuid = this.$useUserStore().duid;

    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      // eslint-disable-next-line no-param-reassign
      task.subscriberDuids = task.subscriberDuids.filter((e) => !subscriberDuids.includes(e));
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, subscriberDuids });
    });

    const backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedSubscribersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(subscriberDuids).map((e) => getUserDisplayName(e))
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: "subscriberDuids" },
        [
          `Unsubscribed ${removedSubscribersStr} from ${content}`,
          `unsubscribing ${removedSubscribersStr} from ${content}`,
        ],
        () => this.addSubscribers(taskDuids, subscriberDuids, { noUndo: true }),
        () => this.removeSubscribers(taskDuids, subscriberDuids, { noUndo: true })
      );
    }
  },
  async addOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, createTitle, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = this.$useUserStore().duid;

    let createdOption: Option | undefined;
    if (createTitle) {
      createdOption = this.$createOptionNoBackend(createTitle, property);
      if (createdOption) {
        optionDuids.push(createdOption.duid);
      }
    }

    const taskUpdates: TaskUpdate[] = [];
    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });
    tasks.forEach((task) => {
      // Sort options alphabetically
      const currentOptionDuids = getPropertyValueFromTask(property, task);
      const newDuids = [...currentOptionDuids, ...optionDuids];

      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        this.getOptionsByDuidsOrdered(newDuids).map((e) => e.duid)
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({
        duid: task.duid,
        ...(isDefault ? { tagDuids: optionDuids } : partialTaskUpdate),
      });
    });

    // TODO subscribe updater

    let transactionResponse: Promise<TransactionResponse>;
    if (isDefault) {
      if (createdOption) {
        transactionResponse = this.$backend.option.createAndTaskUpdateListAddMany(createdOption, taskUpdates);
      } else {
        transactionResponse = this.$backend.task.updateListAddMany(taskUpdates);
      }
    } else if (createdOption) {
      transactionResponse = this.$backend.option.createAndTaskUpdateMany(createdOption, taskUpdates);
    } else {
      transactionResponse = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await transactionResponse;
    }

    if (!noUndo) {
      const addedOptionsStr = prettyFormatList(this.getOptionsByDuidsOrdered(optionDuids).map((t) => t.title));
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Added ${property.title.toLowerCase()} ${addedOptionsStr} to ${content}`,
          `adding  ${property.title.toLowerCase()} ${addedOptionsStr} to ${content}`,
        ],
        () => this.removeOptions(property, taskDuids, optionDuids, { noUndo: true }),
        () => this.addOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
    return transactionResponse;
  },
  async removeOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = this.$useUserStore().duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      const currentOptionDuids = getPropertyValueFromTask(property, task);
      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        currentOptionDuids.filter((e) => !optionDuids.includes(e))
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, ...(isDefault ? { tagDuids: optionDuids } : partialTaskUpdate) });
    });

    // TODO subscribe updater
    let backendAction: Promise<TransactionResponse>;

    if (isDefault) {
      backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    } else {
      backendAction = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedOptionsStr = prettyFormatList(this.getOptionsByDuidsOrdered(optionDuids).map((t) => t.title));
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Removed ${property.title.toLowerCase()} ${removedOptionsStr} from ${content}`,
          `removing ${property.title.toLowerCase()} ${removedOptionsStr} from ${content}`,
        ],
        () => this.addOptions(property, taskDuids, optionDuids, { noUndo: true }),
        () => this.removeOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
  },
  async replaceOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const updaterDuid = this.$useUserStore().duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    const oldOptionDuids: string[] = [];
    tasks.forEach((task) => {
      // Sort options alphabetically
      const currentOptionDuids = getPropertyValueFromTask(property, task);
      oldOptionDuids.push(...currentOptionDuids);

      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        this.getOptionsByDuidsOrdered([...new Set(optionDuids)]).map((e) => e.duid)
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      appStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, ...partialTaskUpdate });
    });

    // TODO subscribe updater

    const backendAction = this.$backend.task.updateMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedOptionsStr = prettyFormatList(this.getOptionsByDuidsOrdered(optionDuids).map((t) => t.title));
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Set ${property.title.toLowerCase()} ${addedOptionsStr} on ${content}`,
          `setting  ${property.title.toLowerCase()} ${addedOptionsStr} on ${content}`,
        ],
        () => this.replaceOptions(property, taskDuids, oldOptionDuids, { noUndo: true }),
        () => this.replaceOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
  },
  async createRelationship(sourceDuid, targetDuid, relationshipKindDuid, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    if (sourceDuid === targetDuid) {
      return undefined;
    }
    const sourceTask = this.getTaskByDuidIfLoaded(sourceDuid);
    const targetTask = this.getTaskByDuidIfLoaded(targetDuid);
    const relationshipKind = this.getRelationshipKindByDuid(relationshipKindDuid);
    if (!sourceTask || !targetTask || !relationshipKind) {
      return undefined;
    }
    const preexistingRelationship = sourceTask.relationships.filter(
      (e) => e.targetDuid === targetDuid && e.kindDuid === relationshipKindDuid
    )[0];
    if (preexistingRelationship && preexistingRelationship.isForward) {
      return preexistingRelationship;
    }

    const descendants = this.getDescendantDuidsIfLoaded(targetTask);
    if (descendants.some((e) => e === sourceDuid)) {
      Sentry.captureMessage(
        `Source task ${sourceDuid} is already a descendant ${relationshipKind.kind} of the target task ${targetDuid}`
      );
      return undefined;
    }

    const duid = makeDuid();
    const forwardRelationship = this.$createRelationshipOneDirectionNoBackend(
      duid,
      sourceDuid,
      targetDuid,
      relationshipKindDuid,
      relationshipKind.directed ? true : undefined
    );
    const backwardRelationship = this.$createRelationshipOneDirectionNoBackend(
      duid,
      targetDuid,
      sourceDuid,
      relationshipKindDuid,
      relationshipKind.directed ? false : undefined
    );
    if (!forwardRelationship || !backwardRelationship) {
      return undefined;
    }

    appStore.addRecentTask(sourceTask);
    appStore.addRecentTask(targetTask);

    // TODO subscribe updater to both tasks

    if (!noBackend) {
      let backendAction: Promise<TransactionResponse>;
      const createObj = { ...forwardRelationship, sourceDuid };
      if (preexistingRelationship) {
        this.deleteRelationship(sourceDuid, preexistingRelationship.duid, { noBackend: true });
        backendAction = this.$backend.relationship.deleteAndCreate(preexistingRelationship.duid, createObj);
      } else {
        backendAction = this.$backend.relationship.create(createObj);
      }

      if (awaitBackend) {
        await backendAction;
      }
    }

    return forwardRelationship;
  },
  async deleteRelationship(sourceDuid, relationshipDuid, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const now = new Date().toISOString();
    const updaterDuid = this.$useUserStore().duid;

    const sourceTask = await this.getTaskByDuid(sourceDuid);
    if (!sourceTask) {
      return;
    }
    const relationship = sourceTask.relationships.find((e) => e.duid === relationshipDuid);
    if (!relationship) {
      return;
    }
    const targetTask = await this.getTaskByDuid(relationship.targetDuid);
    if (!targetTask) {
      return;
    }

    sourceTask.relationships = sourceTask.relationships.filter((e) => e.duid !== relationshipDuid);
    sourceTask.updatedAt = now;
    sourceTask.updatedByDuid = updaterDuid;
    targetTask.relationships = targetTask.relationships.filter((e) => e.duid !== relationshipDuid);
    targetTask.updatedAt = now;
    targetTask.updatedByDuid = updaterDuid;

    appStore.addRecentTask(sourceTask);
    appStore.addRecentTask(targetTask);

    // TODO subscribe updater to both tasks

    let backendAction: Promise<TransactionResponse> | undefined;
    if (!noBackend) {
      backendAction = this.$backend.relationship.delete(relationshipDuid);
    }

    if (awaitBackend && backendAction) {
      await backendAction;
    }
  },
  async deleteAndCreateRelationshipAndUpdateTask(relationshipDelete, relationshipCreate, taskUpdate, options = {}) {
    if (!relationshipDelete && !relationshipCreate && !taskUpdate) {
      return;
    }

    const { noBackend = false, celebrateOverride = false, awaitBackend = false, onRemapFailed = () => {} } = options;

    let deleteDuid: string | undefined;
    let relationshipCreateFormal: RelationshipCreate | undefined;

    if (relationshipDelete) {
      const { taskDuid, relationshipDuid } = relationshipDelete;
      this.deleteRelationship(taskDuid, relationshipDuid, { noBackend: true, awaitBackend });
      deleteDuid = relationshipDuid;
    }

    if (relationshipCreate) {
      const { sourceDuid, targetDuid, relationshipKindDuid } = relationshipCreate;
      const forwardRelationship = await this.createRelationship(sourceDuid, targetDuid, relationshipKindDuid, {
        noBackend: true,
      });
      if (forwardRelationship) {
        relationshipCreateFormal = { ...forwardRelationship, sourceDuid };
      }
    }

    if (taskUpdate) {
      this.updateTasks([taskUpdate], { noUndo: true, noBackend, celebrateOverride, awaitBackend, onRemapFailed });
    }

    // TODO subscribe updater to all (up to 5) tasks

    if (!noBackend) {
      if (deleteDuid || relationshipCreateFormal) {
        const backendAction = this.$backend.relationship.deleteAndCreateAndUpdateTask(
          deleteDuid,
          relationshipCreateFormal
        );

        if (awaitBackend) {
          await backendAction;
        }
      }
    }
  },
  async addAttachments(taskDuid, files, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = this.getTaskByDuidIfLoaded(taskDuid, { assertLoaded: true });
    if (!task) {
      return;
    }

    const lastAttachmentDuid = task.attachmentDuids.at(-1);
    const lastAttachment = lastAttachmentDuid
      ? this.getAttachmentByDuidIfLoaded(lastAttachmentDuid, { assertLoaded: true })
      : undefined;
    const orders = getOrdersBetween(lastAttachment?.order, undefined, files.length);
    const attachmentConfigs = files.map((file, i) => ({
      file,
      order: orders[i],
      partialAttachment: { duid: makeDuid() },
    }));
    const attachmentDuids = attachmentConfigs.map((e) => e.partialAttachment.duid);

    task.attachmentDuids.push(...attachmentDuids);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const attachmentAndCreates = await this.createAttachments(attachmentConfigs, { taskDuid, noBackend: true });

    const taskUpdate: TaskUpdate = {
      duid: taskDuid,
      attachmentDuids,
    };

    const backendAction = this.$backend.task.createAttachmentsAndAdd(
      attachmentAndCreates.map((e) => e.create),
      taskUpdate
    );

    if (awaitBackend) {
      await backendAction;
    }
  },
  async removeAttachment(taskDuid, attachmentDuid, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = this.getTaskByDuidIfLoaded(taskDuid, { assertLoaded: true });
    const attachment = this.getAttachmentByDuidIfLoaded(attachmentDuid, { assertLoaded: true });
    if (!task || !attachment) {
      return;
    }

    task.attachmentDuids = task.attachmentDuids.filter((e) => e !== attachmentDuid);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const taskUpdate: TaskUpdate = {
      duid: taskDuid,
      attachmentDuids: [attachmentDuid],
    };
    this.$addEntityDuidPendingDeletion(attachment.duid);
    const backendAction = this.$backend.task.removeAttachmentAndDelete(taskUpdate, attachmentDuid);

    const action = (async () => {
      await backendAction;
      this.$removeEntityDuidPendingDeletion(attachment.duid);
    })();

    if (awaitBackend) {
      await action;
    }
  },
  async addLink(taskDuid, kind, url, title, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }
    const allOrders = task.links.map((e) => e.order).sort(stringComparator);
    const order = getOrdersBetween(allOrders[allOrders.length - 1], undefined)[0];

    const taskLinkCreate = { duid: makeDuid(), taskDuid: task.duid, kind, order, url, title };

    this.$addLinksNoBackend(task, [taskLinkCreate]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backend.task.link.create(taskLinkCreate);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async updateLink(taskDuid, linkUpdate, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$updateLinksNoBackend(task, [linkUpdate]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    const backendAction = this.$backend.task.link.update(linkUpdate);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async deleteLink(taskDuid, linkDuid, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$deleteLinksNoBackend(task, [linkDuid]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backend.task.link.delete(linkDuid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async addUpdateAndDeleteLinks(taskDuid, creates, updates, deletes, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$addLinksNoBackend(task, creates);
    this.$updateLinksNoBackend(task, updates);
    this.$deleteLinksNoBackend(task, deletes);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater to all tasks

    const backendAction = this.$backend.task.link.addUpdateAndDeleteMany(creates, updates, deletes);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async addNotionDocument(taskDuid, pageId, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return undefined;
    }

    task.notionDocument = {
      pageId,
      refreshing: false,
      existsAndAccessGranted: null,
      lastRefreshAt: null,
      blockMap: null,
      blockChildrenMap: null,
      pageMap: null,
    };
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backendOld.tasks.addNotionDocument(taskDuid, pageId);
    if (awaitBackend) {
      await backendAction;
    }

    return task;
  },
  async getNotionDocument(taskDuid) {
    const task = await this.getTaskByDuid(taskDuid);
    if (!task || !task.notionDocument) {
      return undefined;
    }

    // If the notion document is already cached, return it
    if (task.notionDocument.pageMap) {
      return Promise.resolve(task);
    }

    // Get and cache the notion document
    const { data } = await this.$backendOld.tasks.getNotionDocument(taskDuid);
    task.notionDocument = data.item;
    return task;
  },
  async refreshNotionDocument(taskDuid) {
    const task = await this.getTaskByDuid(taskDuid);
    if (!task || !task.notionDocument) {
      return undefined;
    }

    // Indicate that the notion document is being refreshed
    task.notionDocument.refreshing = true;
    await this.$backendOld.tasks.refreshNotionDocument(taskDuid);
    if (task.notionDocument) {
      task.notionDocument.refreshing = false;
    }

    return task;
  },
  async removeNotionDocument(taskDuid, options = {}) {
    const { awaitBackend = false } = options;

    const appStore = this.$useAppStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return undefined;
    }

    task.notionDocument = null;
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    appStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backendOld.tasks.removeNotionDocument(taskDuid);
    if (awaitBackend) {
      await backendAction;
    }

    return task;
  },
  async $replicateTaskRecursive(task, order, partialTask, options = {}) {
    const {
      propertiesAndParentOnly = false,
      includeDuplicate = false,
      maintainTitle = false,
      maintainStatus = false,
    } = options;
    const res = {
      taskCreates: [] as TaskCreate[],
      relationshipCreates: [] as RelationshipCreate[],
      taskDocRelationshipCreates: [] as TaskDocRelationshipCreate[],
      linkCreates: [] as TaskLinkCreate[],
      attachmentCreates: [] as AttachmentCreate[],
    };

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    const duplicateKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.DUPLICATES).duid;

    // Add the task itself
    const now = new Date().toISOString();
    const replicatedTask: Task = {
      ...deepCopy(task),
      duid: makeDuid(),
      createdAt: now,
      updatedAt: now,
      order,
      recommendationDuid: null,
      subscriberDuids: [...new Set([...task.subscriberDuids, this.$useUserStore().duid])].sort(),
      links: [],
      attachmentDuids: [],
      relationships: [],
      docRelationships: [],
      notionDocument: null,
      timeTracking: [],
      recurrence: null,
      recursNextAt: null,
      ...partialTask,
    };
    if (!maintainTitle && partialTask?.title === undefined) {
      replicatedTask.title = getNextTitleInSequence(
        task.title,
        this.getLoadedTasksForPageDuidOrdered(replicatedTask.dartboardDuid).map((e) => e.title)
      );
    }
    if (!maintainStatus && partialTask?.statusDuid === undefined) {
      replicatedTask.statusDuid = this.defaultDefaultUnstartedStatus.duid;
    }
    replicatedTask.relationships = replicatedTask.relationships.map((e) => ({
      ...e,
      duid: makeDuid(),
      sourceDuid: replicatedTask.duid,
    }));

    this.$createOrUpdateTask(replicatedTask);
    res.taskCreates.push({ ...replicatedTask, sourceKind: TaskSourceKind.APP_REPLICATE, sourceTaskDuid: task.duid });

    res.relationshipCreates.push(
      ...replicatedTask.relationships
        .map(({ duid, kindDuid, targetDuid, isForward }) => [
          this.$createRelationshipOneDirectionNoBackend(
            duid,
            targetDuid,
            replicatedTask.duid,
            kindDuid,
            isForward !== undefined ? !isForward : undefined
          ),
          targetDuid,
        ])
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }))
    );

    if (!propertiesAndParentOnly) {
      // Add the task - doc relationships
      res.taskDocRelationshipCreates.push(
        ...(await this.getDocsRelatedToTaskOrdered(task)).map((doc) => {
          const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid: replicatedTask.duid, docDuid: doc.duid };
          this.$createTaskDocRelationship(taskDocRelationshipCreate);
          return taskDocRelationshipCreate;
        })
      );

      // Add the links
      res.linkCreates.push(
        ...task.links
          .filter((e) => e.kind === TaskLinkKind.STANDARD)
          .map((link) => {
            const taskLinkCreate = { ...link, duid: makeDuid(), taskDuid: replicatedTask.duid };
            this.$addLinksNoBackend(replicatedTask, [taskLinkCreate]);
            return taskLinkCreate;
          })
      );

      // Add the attachments
      const oldAttachments = await this.getAttachmentsByDuidsOrdered(task.attachmentDuids);
      const files = await createFilesFromAttachments(oldAttachments);
      const attachmentConfigs = oldAttachments.map((attachment, i) => ({
        file: files[i],
        order: attachment.order,
        partialAttachment: { colorHex: oldAttachments[i].colorHex },
      }));
      const attachmentsAndCreates = await this.createAttachments(attachmentConfigs, {
        taskDuid: replicatedTask.duid,
        noBackend: true,
      });
      replicatedTask.attachmentDuids.push(...attachmentsAndCreates.map((e) => e.attachment.duid));
      res.attachmentCreates.push(...attachmentsAndCreates.map((e) => e.create));

      // Replicate the non parent and non duplicate relationships
      const newRelationships = this.getRelationships(task)
        .filter((e) => e.kindDuid !== parentKindDuid && e.kindDuid !== duplicateKindDuid)
        .map((relationship) => {
          // TODO unclear why reactive is needed here. should figure it out and remove
          const newRelationship = reactive({ ...relationship, duid: makeDuid() });
          replicatedTask.relationships.push(newRelationship);
          return [
            this.$createRelationshipOneDirectionNoBackend(
              newRelationship.duid,
              newRelationship.targetDuid,
              replicatedTask.duid,
              newRelationship.kindDuid,
              newRelationship.isForward !== undefined ? !newRelationship.isForward : undefined
            ),
            relationship.targetDuid,
          ];
        })
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }));
      res.relationshipCreates.push(...newRelationships);

      // Add child tasks recursively
      const subtaskRelationships = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, true);
      const subtaskOrders = getOrdersBetween(replicatedTask.order, undefined, subtaskRelationships.length);

      await Promise.all(
        subtaskRelationships.map(async (relationship, i) => {
          const subtask = await this.getTaskByDuid(relationship.targetDuid);
          if (!subtask) {
            return;
          }

          const subtaskRes = await this.$replicateTaskRecursive(
            subtask,
            subtaskOrders[i],
            { ...partialTask, relationships: [] },
            {
              ...options,
              maintainTitle: true,
              maintainStatus,
            }
          );
          res.taskCreates.push(...subtaskRes.taskCreates);
          res.relationshipCreates.push(...subtaskRes.relationshipCreates);
          res.taskDocRelationshipCreates.push(...subtaskRes.taskDocRelationshipCreates);
          res.linkCreates.push(...subtaskRes.linkCreates);
          res.attachmentCreates.push(...subtaskRes.attachmentCreates);

          const replicatedSubtask = subtaskRes.replicatedTask;
          const newRelationship = await this.createRelationship(
            replicatedTask.duid,
            replicatedSubtask.duid,
            parentKindDuid,
            { noBackend: true }
          );
          if (newRelationship) {
            res.relationshipCreates.push({ ...newRelationship, sourceDuid: replicatedTask.duid });
          }
        })
      );
    }

    if (!propertiesAndParentOnly || includeDuplicate) {
      // TODO unclear why reactive is needed here. should figure it out and remove
      const newRelationshipRaw = reactive({
        duid: makeDuid(),
        kindDuid: duplicateKindDuid ?? "",
        targetDuid: task.duid,
      });
      const newRelationship = this.$createRelationshipOneDirectionNoBackend(
        newRelationshipRaw.duid,
        newRelationshipRaw.targetDuid,
        replicatedTask.duid,
        newRelationshipRaw.kindDuid,
        undefined
      );
      if (newRelationship) {
        replicatedTask.relationships.push(newRelationshipRaw);
        res.relationshipCreates.push({ ...newRelationship, sourceDuid: task.duid });
      }
    }

    return { ...res, replicatedTask };
  },
  $createRelationshipOneDirectionNoBackend(
    relationshipDuid,
    modTaskDuid,
    otherTaskDuid,
    relationshipKindDuid,
    isForward
  ) {
    const task = this.getTaskByDuidIfLoaded(modTaskDuid);
    if (!task) {
      return undefined;
    }

    // TODO unclear why reactive is needed here. should figure it out and remove
    const relationship = reactive({
      duid: relationshipDuid,
      kindDuid: relationshipKindDuid,
      targetDuid: otherTaskDuid,
      isForward,
    });
    task.relationships.push(relationship);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    return relationship;
  },
  $updateTaskSubscribersNoBackend(task, potentialSubscriberDuids) {
    const currentSubscriberDuids = new Set(task.subscriberDuids);
    const newSubscriberDuids = potentialSubscriberDuids.filter((e) => !currentSubscriberDuids.has(e));

    // eslint-disable-next-line no-param-reassign
    task.subscriberDuids = [...currentSubscriberDuids, ...newSubscriberDuids];

    return newSubscriberDuids;
  },
  $addLinksNoBackend(task, linkCreates) {
    // TODO unclear why reactive is needed here. should figure it out and remove
    task.links.push(
      ...linkCreates.map((e) =>
        reactive({
          kind: TaskLinkKind.STANDARD,
          title: null,
          iconUrl: null,
          adtl: {},
          ...e,
        })
      )
    );
  },
  $updateLinksNoBackend(task, linkUpdates) {
    linkUpdates.forEach((linkUpdate) => {
      const link = task.links.find((e) => e.duid === linkUpdate.duid);
      if (!link) {
        return;
      }

      Object.assign(link, linkUpdate);
    });
  },
  $deleteLinksNoBackend(task, linkDuids) {
    filterInPlace(task.links, (e: TaskLink) => !linkDuids.includes(e.duid));
  },
  $createOrUpdateTask(task, extras = {}) {
    const { hardUpdate = false, skipSummaryStatistics = false } = extras;

    /* Shared */
    const appStore = this.$useAppStore();
    const pageStore = this.$usePageStore();

    const currentTask = this.getTaskByDuidIfLoaded(task.duid);
    if (this.$isEntityDuidPendingDeletion(task.duid)) {
      return;
    }

    if (
      !appStore.tcmOpen &&
      currentTask &&
      currentTask.createdByDuid &&
      this.getUserByDuid(currentTask.createdByDuid)?.kind === USER_KIND_DART_AI &&
      currentTask.recommendationDuid
    ) {
      const visualization = appStore.getActiveVisualization();
      const taskElement = visualization?.getHtml(currentTask.duid);
      if (!taskElement) {
        return;
      }
      appStore.showFeedbackTooltip(taskElement, [currentTask.recommendationDuid]);
    }

    // TODO remove this check when we are filtering out spurious tasks on BE of WS
    if (
      (!pageStore.isPublicView && !this.getDartboardByDuid(task.dartboardDuid)) ||
      (task.drafterDuid && task.drafterDuid !== this.$useUserStore().duid)
    ) {
      if (currentTask) {
        this.$deleteTask(currentTask);
      }
      return;
    }

    // Needs to happen before updates to pagination states
    if (!skipSummaryStatistics) {
      if (!currentTask) {
        this.$updateSummaryStatisticsForTaskChange(undefined, task);
      } else {
        this.$updateSummaryStatisticsForTaskChange(currentTask, { ...currentTask, ...task });
      }
    }

    this.$unblacklistEntityDuid(task.duid);
    this.$maybeRemoveInvalidEntityFromPaginationStates(task);

    // Also re-validate all ancestors since they might be absentee parents that no longer qualify
    const ancestorDuids = this.getAncestorDuidsIfLoaded(task);
    const ancestors = this.getTasksByDuidsIfLoaded(ancestorDuids);
    ancestors.forEach((ancestor) => {
      this.$maybeRemoveInvalidEntityFromPaginationStates(ancestor);
    });

    if (!task.drafterDuid) {
      this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
    }

    // Add attachments to pagination state if attachment pagination exists for this task
    const attachmentPaginationKey = `attachments:${task.duid}`;
    const attachmentPaginationState = this.$getPaginationState(attachmentPaginationKey, null);
    if (attachmentPaginationState && task.attachmentDuids.length > 0) {
      const attachments = task.attachmentDuids
        .map((attachmentDuid) => this.getAttachmentByDuidIfLoaded(attachmentDuid))
        .filter(isDefined);
      if (attachments.length > 0) {
        this.$addEntitiesToPaginationStates(attachmentPaginationKey, attachments);
      }
    }

    /* Create */
    if (!currentTask) {
      this._duidsToTasks.set(task.duid, task);
      if (this.$isPrimaryDraft(task)) {
        this.taskDraftDuid = task.duid;
      }
      return;
    }

    /* Update */
    // Capture old task state before updating for dead-reckoning
    Object.assign(currentTask, task);

    if (this.$isPrimaryDraft(currentTask)) {
      this.taskDraftDuid = currentTask.duid;
    } else if (this.taskDraft && this.taskDraft.duid === currentTask.duid) {
      this.taskDraftDuid = null;
    }

    if (hardUpdate) {
      this.$useAppStore().taskDetail?.hardUpdateDescription();
    }
  },
  $deleteTask(task) {
    this._duidsToTasks.delete(task.duid);
    this.$updateSummaryStatisticsForTaskChange(task, undefined);
    this.$removeEntitiesFromPaginationStates([task]);

    if (this.taskDraft && this.taskDraft.duid === task.duid) {
      this.taskDraftDuid = null;
    }

    this.$useAppStore().selectedTaskDuids.delete(task.duid);
  },
  $isPrimaryDraft(task) {
    if (
      !task.drafterDuid ||
      (task.createdByDuid && this.getUserByDuid(task.createdByDuid)?.kind === USER_KIND_DART_AI)
    ) {
      return false;
    }
    const parentDuids = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false);
    if (parentDuids.length === 0) {
      return true;
    }
    const parent = this.getTaskByDuidIfLoaded(parentDuids[0].targetDuid);
    if (!parent) {
      return true;
    }
    return !parent.drafterDuid;
  },
  $setDraftTask(task) {
    this._duidsToTasks.set(task.duid, task);
    if (this.$isPrimaryDraft(task)) {
      this.taskDraftDuid = task.duid;
    }
  },
  $remapPropertiesOfTasksAsNeeded(updates, onRemapFailed) {
    if (updates.length === 0) {
      return Promise.resolve([]);
    }

    const appStore = this.$useAppStore();

    // Find all updates that need remapping
    const remapModalItems: Map<string, RemapModalConfigItem> = new Map();
    updates.forEach((update) => {
      const suggestedUpdate = checkTaskConstraints(update, this);

      // Remap default status for task kind updates
      if (suggestedUpdate.statusDuid) {
        const task = this.getTaskByDuidIfLoaded(update.duid);
        if (!task) {
          return;
        }

        const groupByDefinition = appStore.groupByDefinitionList.find(
          (e) => e.property.duid === this.defaultStatusProperty.duid
        );
        if (!groupByDefinition) {
          return;
        }

        const groups = groupByDefinition.groups.map((e) => ({ ...e, icon: markRaw(e.icon) }));
        const oldStatusDuid = update.statusDuid ?? task.statusDuid;
        const oldOption = groups.find((e) => e.value === oldStatusDuid);
        if (!oldOption) {
          return;
        }

        const taskKind = this.getTaskKindByDuid(update.kindDuid ?? task.kindDuid);
        const hiddenStatusDuids = new Set(taskKind?.hiddenStatusDuids ?? []);

        remapModalItems.set(`${this.defaultStatusProperty.duid}-${oldStatusDuid}`, {
          propertyDuid: this.defaultStatusProperty.duid,
          oldOption,
          options: groups.filter((e) => typeof e.value === "string" && !hiddenStatusDuids.has(e.value)),
          proposedValue: suggestedUpdate.statusDuid,
        });
      }
    });

    // No remapping needed
    if (remapModalItems.size === 0) {
      return Promise.resolve(updates);
    }

    // Show the remap modal
    const remapPromise = new Promise<TaskUpdate[]>((resolve) => {
      appStore.remapModal = {
        items: Array.from(remapModalItems.values()),
        remap: (items) => {
          resolve(
            [...updates].map((update) => {
              const res = { ...update };
              const task = this.getTaskByDuidIfLoaded(update.duid);
              if (!task) {
                return res;
              }

              let remappedUpdate;
              // Remap default status for task kind updates
              const taskKindDuid = update.kindDuid ?? this.getTaskByDuidIfLoaded(update.duid)?.kindDuid;
              if (taskKindDuid) {
                const oldStatusDuid = update.statusDuid ?? task.statusDuid;
                remappedUpdate = items.find(
                  (e) => e.propertyDuid === this.defaultStatusProperty.duid && e.oldValue === oldStatusDuid
                );
                if (remappedUpdate) {
                  res.statusDuid = remappedUpdate.newValue as string;
                }
              }

              return res;
            })
          );
        },
        cancel: () => {
          resolve([]);
          onRemapFailed();
        },
      };
    });

    return remapPromise;
  },
  $updateSummaryStatisticsForTaskChange(oldTask: Task | undefined, newTask: Task | undefined) {
    const appStore = this.$useAppStore();
    const isCompleted = (task: Task) => {
      const status = this.getStatusByDuid(task.statusDuid);
      return status && COMPLETED_STATUS_KINDS.has(status.kind);
    };

    const updateStatsForTask = (key: string, task: Task, isRemoving: boolean) => {
      const currentStats = this._keyToSummaryStatistics.get(key);
      if (!currentStats) {
        return;
      }

      const newStats = { ...currentStats };

      const multiplier = isRemoving ? -1 : 1;
      const taskSize = task.size ?? 0;
      const taskCompleted = isCompleted(task);

      newStats.count += multiplier;
      newStats.points += multiplier * taskSize;

      if (taskCompleted) {
        newStats.completedCount += multiplier;
        newStats.completedPoints += multiplier * taskSize;
      }

      // Ensure no negative values
      Object.entries(newStats).forEach(([e, value]) => {
        Object.assign(newStats, { [e]: Math.max(0, value) });
      });

      this._keyToSummaryStatistics.set(key, newStats);
    };

    const getGroupKeysForTask = (task: Task, pageDuid: string, groupByPropertyDuid: string): string[] => {
      const propertyValue = getPropertyValueFromTaskByDuid(groupByPropertyDuid, task);
      if (propertyValue === undefined) {
        return [];
      }

      const propertyValueArr = Array.isArray(propertyValue)
        ? propertyValue.length === 0
          ? ["@null"]
          : propertyValue
        : [propertyValue];

      return propertyValueArr.map((value) => getPageKey(pageDuid, getPropertyValueStr(value as PropertyValue)));
    };

    Array.from(this._keyToSummaryStatistics.keys()).forEach((key) => {
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);
      const pageDuid = getPageDuidFromKey(key);
      const parsedKey = JSON.parse(key);
      const groupId = parsedKey[1];

      if (groupId === null || groupId === UNASSIGNED_PSEUDO_ASSIGNEE_KEY) {
        if (!paginationState || !paginationState.validator) {
          return;
        }
        if (oldTask && paginationState.duids.has(oldTask.duid)) {
          updateStatsForTask(key, oldTask, true);
        }
        if (newTask && paginationState.validator(newTask)) {
          updateStatsForTask(key, newTask, false);
        }
        return;
      }

      if (appStore.groupBy && appStore.groupBy !== UNGROUPED_PSEUDO_GROUP_BY) {
        // Check if task is in group pagination state or main pagination state if hasn't been loaded into group pagination state
        const mainPaginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, getPageKey(pageDuid, null));

        if (
          oldTask &&
          ((paginationState && paginationState.duids.has(oldTask.duid)) ||
            (mainPaginationState && mainPaginationState.duids.has(oldTask.duid)))
        ) {
          const oldGroupKeys = getGroupKeysForTask(oldTask, pageDuid, appStore.groupBy);
          if (oldGroupKeys.includes(key)) {
            updateStatsForTask(key, oldTask, true);
          }
        }
        if (
          newTask &&
          ((paginationState?.validator && paginationState.validator(newTask)) ||
            (mainPaginationState?.validator && mainPaginationState.validator(newTask)))
        ) {
          const newGroupKeys = getGroupKeysForTask(newTask, pageDuid, appStore.groupBy);
          if (newGroupKeys.includes(key)) {
            updateStatsForTask(key, newTask, false);
          }
        }
      }
    });
  },
};

export { actions, getters };
